import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import intl from "react-intl-universal";
import { mdiContentDuplicate, mdiAdjust } from "@mdi/js";

import { theme } from "../../../../../components/GlobalStyle";
import commonUtils from "../../../../../../script/utils/commonUtils";
import entryRest from "../../../../../../rest/entryRest";
import worstcaseRest from "../../../../../../rest/worstcaseRest";
import fieldArrayResultUtils from "../../../../../../script/utils/fieldArrayResultUtils";

import { Legend } from "../../Legend";
import { WorstcaseItem } from "./WorstcaseItem";
import { ResultViewType } from "../ResultViewType";
import { WorstcaseDetail } from "./WorstcaseDetail";
import { Grid } from "../../../../../components/Grid";
import { Button } from "../../../../../components/Button";
import { Spacer } from "../../../../../components/Spacer";
import { BoxList } from "../../../../../components/BoxList";
import { WorstcaseResultDialog } from "./WorstcaseResultDialog";
import { EntryContext } from "../../../../../contexts/EntryContext";
import calculationRest from "../../../../../../rest/calculationRest";
import plotUtils from "../../../../../../script/utils/plotUtils";
import { useSaveEntry } from "../../../useSaveEntry";

const LayerStyle = styled.div`
    display: ${props => (props.$show ? "flex" : "none")};
`;

const DetailStyle = styled.div`
    margin-top: 15px;
    padding-top: 15px;
    height: 38%;
    border-top: 1px solid ${theme.greyLight};
`;

export const WorstcaseLayer = props => {
    const { plot, show, calculationTypes = ["WORSTCASE"] } = props;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { calcSetting } = entry;

    const layerName = "worstcaseLayer";
    const pointLayerName = "pointLayer";

    const initLoading = useRef(true);

    const [detail, setDetail] = useState();
    const [areaName, setAreaName] = useState("");
    const [calculations, setCalculations] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState();
    const [resultDialogCalculation, setResultDialogCalculation] = useState();
    const resultDetails = useRef({});
    const [loadingSlice, setLoadingSlice] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(false);
    const [legendData, setLegendData] = useState();
    const {saving, saveEntry} = useSaveEntry();

    const calculationsRef = useRef(calculations);
    calculationsRef.current = calculations;

    const getCalculationId = object =>
        object?.calculationId
        ?? object?.getObjects?.()
            ?.map(getCalculationId)
            .find(id => id !== undefined);

    const getCalculationIdFromEvent = opt =>
        getCalculationId(opt?.selected?.[0])
        ?? getCalculationId(opt?.target)
        ?? opt?.subTargets
            ?.map(getCalculationId)
            .find(id => id !== undefined);

    const selectCalculationById = calculationId => {
        const index = calculationsRef.current.findIndex(c => c.id === calculationId);
        setSelectedIndex(index >= 0 ? index : undefined);
        return index >= 0 ? calculationsRef.current[index] : undefined;
    };

    const selectedCalculation = selectedIndex !== undefined ? calculations[selectedIndex] : undefined;

    const isCalcInProgress = entry?.calcSetting?.calcAreas?.some(calcArea =>
        calcArea.calculations?.some(calculation => !calculation.resultAvailable)
    ) || false;

    const onResultClicked = (opt, calculationId) => {
        const calculation = selectCalculationById(calculationId);
        if (!calculation) {
            setSelectedPoint(false);
            return;
        }

        const layer = plot.getLayer(pointLayerName);
        layer.removeObjects();
        plot.addCircle(layer, opt.scenePoint.x, opt.scenePoint.y, true);

        const {ratioU, ratioV} = plotUtils.calculteAreaSidesClickRatio(opt);
        setSelectedPoint({area: calculation.calcArea, ratioU, ratioV});
    };

    useEffect(() => {
        const pointLayer = plot.addLayer(pointLayerName, 180);
        const layer = plot.addLayer(layerName, 110);
        layer.setHandler("selection:created", opt => selectCalculationById(getCalculationIdFromEvent(opt)));
        layer.setHandler("selection:updated", opt => selectCalculationById(getCalculationIdFromEvent(opt)));
        layer.onDeactivate(deactivatingLayer => {
            if (!["measureLayer", "downloadLayer"].includes(deactivatingLayer.name)) {
                layer.removeObjects();
                pointLayer.removeObjects();
                plot.removeColorBar();
                setSelectedIndex();
                setSelectedPoint(false);
                setDetail();
                setAreaName("");
            }
        });
        layer.setHandler("mouse:down", opt => {
            const calculationId = getCalculationIdFromEvent(opt);
            if (calculationId === undefined) {
                plot.canvas.discardActiveObject();
                plot.canvas.renderAll();
                setSelectedIndex();
                setSelectedPoint(false);
                setDetail();
                setAreaName("");
            } else {
                onResultClicked(opt, calculationId);
            }
        });
    }, []);

    useEffect(() => {
        if (!isCalcInProgress && show) {
            plot.getLayer(layerName).activate();
            filterCalculations();
        } else {
            initLoading.current = true;
            setSelectedIndex();
            setSelectedPoint(false);
            setDetail();
            setAreaName("");
        }
    }, [show, calculationTypes.join(","), isCalcInProgress]);

    const loadAllSlices = slice => {
        const layer = plot.getLayer(layerName);
        for (let index = 0; index < calculations.length; index++) {
            const c = calculations[index];
            drawSlice(index, layer, c.calcArea, c, slice);
        }
    };

    useEffect(() => {
        const slice = calcSetting.imageSetting.slice !== undefined ? calcSetting.imageSetting.slice : 0;
        if (show && calculations.length > 0 && initLoading.current) {
            loadAllSlices(slice);
            initLoading.current = false;
        }
    }, [calculations]);

    useEffect(() => {
        const slice = calcSetting.imageSetting.slice !== undefined ? calcSetting.imageSetting.slice : 0;

        if (selectedCalculation) {
            loadColorBar(selectedCalculation.id, slice);
            loadDetail(selectedCalculation.id, slice);
        } else {
            plot.removeColorBar();
            setDetail();
            setAreaName("");
        }
    }, [selectedIndex, calculations]);

    const loadColorBar = async (calculationId, slice) => {
        plot.removeColorBar();
        if (calculationId) {
            const colorBarData = await worstcaseRest.getColorBar(calculationId, slice);
            plot.removeColorBar();
            plot.addColorBar(colorBarData);
        }
    };

    const {drawTopViewResult, onBoundaryClipped} = fieldArrayResultUtils;

    const loadDetail = async (calculationId, slice) => {
        if (calculationId) {
            const calc = calculations.find(c => c.id === calculationId);
            setAreaName(calc?.calcArea?.name);
            const detail = await worstcaseRest.getDetailData(calculationId, slice);
            setDetail(detail);
        } else {
            setDetail();
            setAreaName("");
        }
        plot.canvas.renderAll();
    };

    const filterCalculations = () => {
        const layer = plot.getLayer(layerName);
        layer.removeObjects();

        const pointLayer = plot.getLayer(pointLayerName);
        pointLayer.removeObjects();

        plot.removeColorBar();
        setSelectedIndex();
        setSelectedPoint(false);
        setDetail();
        setAreaName("");

        const calculations = calcSetting.calcAreas
            .filter(calcArea => calcArea.calculations !== undefined)
            .flatMap(calcArea => calcArea.calculations.map(calculation => ({ calcArea, calculation })))
            .filter(({ calculation }) => calculationTypes.includes(calculation.calculationType))
            .map(({ calcArea, calculation }) => ({ ...calculation, calcArea }));
        const calcsInDisplayOrderAndAreaHeightOrder = sortCalculations(calculations);
        setCalculations(calcsInDisplayOrderAndAreaHeightOrder);
    };

    const calcAreasIdsToPointAnalysisResultMap = useMemo(() => {
        const map = new Map();
        calcSetting.calcAreas.forEach(ca =>
            map.set(ca.id, ca.calculations?.find(c => c.worstcasePointResult)?.worstcasePointResult)
        );
        return map;
    },[entry]);

    const drawSlice = async (index, layer, calcArea, calculation, slice) => {
        layer.removeObjects();
        setLoadingSlice(true);
        const result = await worstcaseRest.getResult(calculation.id, ResultViewType.TOP, slice);
        resultDetails.current[calculation.id] = result.details;
        const pointAnalysisResult = calcAreasIdsToPointAnalysisResultMap.get(calcArea.id);
        drawTopViewResult(
            plot,
            setLegendData,
            index,
            layer,
            calculation,
            calcArea,
            result,
            calculation.worstcaseResult,
            slice,
            false,
            pointAnalysisResult,
            calcArea.worstcaseCalcPoint,

        );
        setLoadingSlice(false);
    };

    const onDuplicate = async () => {
        if (!selectedCalculation) {
            return;
        }

        await entryRest.duplicateApplyWorstcase(entry.id, selectedCalculation.id);
    };

    const sortCalculations = calculations =>
        calculations.sort((c, n) => (c.worstcaseResult?.displayOrder ?? 100) - (n.worstcaseResult?.displayOrder ?? 100));

    const orderResults = calculations => {
        const layer = plot.getLayer(layerName);
        const allObjects = layer.getObjects();
        const reversedCalculations = [...calculations];
        reversedCalculations.reverse();
        for (const calculation of reversedCalculations) {
            const resultObjects = allObjects.filter(im => im.calculationId === calculation.id);
            for(const obj of resultObjects) {
                layer.removeObject(obj);
                layer.addObject(obj);
            }
        }
    };

    const updateCalculations = calculations => {
        const calcIds = calculations.map(c => c.id);
        for (const calcArea of entry.calcSetting.calcAreas) {
            for (const calculation of calcArea.calculations) {
                const index = calcIds.indexOf(calculation.id);
                if (index > -1 && calculation.worstcaseResult) {
                    const localCalculation = calculations[index];
                    calculation.worstcaseResult.displayOrder = localCalculation.worstcaseResult.displayOrder;
                    calculation.worstcaseResult.shouldDisplay = localCalculation.worstcaseResult.shouldDisplay;
                }
            }
        }
        entryContext.setEntry(entry);
    };

    const setPointAnalysisVisibility = (index, value) => {
        const calculation = calculations[index];
        const {calcArea} = calculation;
        calcAreasIdsToPointAnalysisResultMap.get(calcArea.id).visible = value;
        const layer = plot.getLayer(layerName);
        layer.getObjects().filter(o => o.calculationId === calculation.id && o.mxwellname === "operator ratio")
            .forEach(obj => obj.set({ visible: value && calculation.worstcaseResult.shouldDisplay }));
        plot.canvas.renderAll();
    };

    const setResultVisibility = (index, value) => {
        const calculation = calculations[index];
        const {calcArea} = calculation;
        calculation.worstcaseResult.shouldDisplay = value;
        updateCalculations(calculations);
        const layer = plot.getLayer(layerName);
        layer.getObjects().filter(o => o.calculationId === calculation.id).forEach(obj => obj.set({ visible: value }));
        const pointAnalysisResult = calcAreasIdsToPointAnalysisResultMap.get(calcArea.id);
        if(pointAnalysisResult) {
            setPointAnalysisVisibility(index, pointAnalysisResult.visible);
        }
        plot.canvas.renderAll();
    };

    const onBoundaryClippedChanged = (index, value) => {
        const calculation = calculations[index];
        const {calcArea} = calculation;
        calculation.worstcaseResult.boundaryClipped = value;
        updateCalculations(calculations);
        const layer = plot.getLayer(layerName);
        onBoundaryClipped(value, layer, calculation, calcArea, plot, calculation.worstcaseResult, resultDetails.current[calculation.id], true);
        plot.canvas.renderAll();
    };

    const onChangeOrder = (fromIndex, toIndex) => {
        const selectedCalculationId = selectedCalculation?.id;
        const updatedCalculations = [...calculations];
        const [movedItem] = updatedCalculations.splice(fromIndex, 1);
        updatedCalculations.splice(toIndex, 0, movedItem);
        updatedCalculations.forEach((c, i) => {
            c.worstcaseResult.displayOrder = i;
        });
        updateCalculations(updatedCalculations);
        setCalculations(updatedCalculations);
        setSelectedIndex(
            selectedCalculationId !== undefined
                ? updatedCalculations.findIndex(c => c.id === selectedCalculationId)
                : undefined
        );
        orderResults(updatedCalculations);
    };

    const onSelect = index => {
        const calculation = calculations[index];
        const layer = plot.getLayer(layerName);
        const result = layer.getObjects()
            .find(o => o.calculationId === calculation.id);
        if (result !== undefined) {
            result.set({ hasControls: false });
            plot.canvas.setActiveObject(result);
            plot.canvas.renderAll();
            selectCalculationById(calculation.id);
        }
    };

    const onChange = imageSetting => {
        const slice = imageSetting.slice !== undefined ? imageSetting.slice : 0;
        loadAllSlices(slice);
        if (selectedCalculation) {
            loadColorBar(selectedCalculation.id, slice);
            loadDetail(selectedCalculation.id, slice);
        }
        entry.imageSetting = imageSetting;
        entryContext.setEntry(entry);
    };

    const startPointAnalysis = async () => {
        if (!selectedPoint?.area?.id) {
            return;
        }
        if (commonUtils.hasRadioWithoutOperator(entry)) {
            commonUtils.dispatchEvent("exception", { i18nLabel: "missingOperator" });
            return;
        }
        await saveEntry(entry);
        const {area, ratioU, ratioV} = selectedPoint;
        const newEntry = await calculationRest.worstcasePoint(entry.id, area.id, ratioU, ratioV, entry.calcSetting.imageSetting.slice);
        entryContext.setEntry(newEntry);
    };

    return (
        <LayerStyle $show={show}>
            <Grid columns="1">
                <Button
                    id="confirmButton"
                    simulateLoading
                    disabled={!selectedCalculation}
                    svgIcon={mdiContentDuplicate}
                    value={intl.get("entry.plan.worstcase.createNew")}
                    onClick={() => onDuplicate()} />
                <Button
                    simulateLoading
                    disabled={!selectedPoint?.area?.id || saving}
                    svgIcon={mdiAdjust}
                    value={intl.get("entry.plan.worstcase.analysePoint")}
                    onClick={() => startPointAnalysis()} />
            </Grid>
            <Legend
                portal
                legendData={
                    legendData && selectedCalculation
                        ? {
                            ...legendData,
                            calculation: selectedCalculation,
                            opt: {
                                ...legendData.opt,
                                target: plot.canvas.getActiveObject() ?? legendData.opt.target,
                            },
                        }
                        : legendData
                }
                viewType={ResultViewType.TOP}
                calcSetting={calcSetting}
                plot={plot} />
            <Spacer variant="horizontal" size="small" />
            {show &&
                <BoxList
                    selectedIndex={selectedIndex}
                    onSelect={onSelect}
                    onChangeOrder={onChangeOrder}>
                    {calculations.map((c, index) => (
                        <WorstcaseItem
                            key={c.id}
                            index={index}
                            calcArea={c.calcArea}
                            shouldDisplay={c.worstcaseResult.shouldDisplay}
                            onVisibilityChanged={val => setResultVisibility(index, val)}
                            onResultDialog={() => setResultDialogCalculation(c)}
                            boundaryClipped={c.worstcaseResult.boundaryClipped}
                            resultDetails={resultDetails.current[c.id]}
                            onBoundaryClippedChanged={val => onBoundaryClippedChanged(index, val)}
                            pointAnalysisResultVisible={calcAreasIdsToPointAnalysisResultMap.get(c.calcArea.id)?.visible}
                            onPointAnalysisResultVisibleChanged={val => setPointAnalysisVisibility(index, val)}/>

                    ))}
                </BoxList>
            }
            {detail && detail.length > 0 &&
                <DetailStyle>
                    <WorstcaseDetail
                        onChange={onChange}
                        detail={detail}
                        areaName={areaName}
                        calcSetting={calcSetting}
                        disabled={loadingSlice}/>
                </DetailStyle>
            }
            {resultDialogCalculation &&
                <WorstcaseResultDialog
                    entry={entry}
                    calcSetting={calcSetting}
                    calculation={resultDialogCalculation}
                    onClose={() => setResultDialogCalculation(undefined)} />
            }
        </LayerStyle>
    );
};